/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1700179200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Nov 17 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "alloydb",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Networking",
          "depth": 1,
          "id": "networking"
        }, {
          "value": "SSH tunneling",
          "depth": 1,
          "id": "ssh-tunneling"
        }, {
          "value": "Required permissions",
          "depth": 1,
          "id": "required-permissions"
        }, {
          "value": "Syncing",
          "depth": 0,
          "id": "syncing"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Column types",
          "depth": 1,
          "id": "column-types"
        }, {
          "value": "Batch size",
          "depth": 1,
          "id": "batch-size"
        }, {
          "value": "Insert mode",
          "depth": 1,
          "id": "insert-mode"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      h3: "h3",
      p: "p",
      a: "a",
      ul: "ul",
      li: "li",
      code: "code",
      strong: "strong"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsx(_components.h3, {
        id: "networking",
        children: "Networking"
      }), "\n", _jsxs(_components.p, {
        children: ["AlloyDB supports network connectivity through private, internal IP addresses configured for private services access.\nTo connect to AlloyDB from Hightouch, configure an SSH connection tunnel to go through a bastion host in your VPC.\nRead about more options here: ", _jsx(_components.a, {
          href: "https://cloud.google.com/alloydb/docs/connection-overview",
          children: "https://cloud.google.com/alloydb/docs/connection-overview"
        })]
      }), "\n", _jsx(_components.h3, {
        id: "ssh-tunneling",
        children: "SSH tunneling"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch can connect directly to AlloyDB over the public internet or via an SSH tunnel. Since data is encrypted in transit via TLS, a direct connection is suitable for most use cases. You may need to set up a tunnel if your AlloyDB instance is on a private network or virtual private cloud (VPC)."
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports both standard and reverse SSH tunnels. To learn more about SSH tunneling, refer to Hightouch's ", _jsx(_components.a, {
          href: "/security/ssh-tunneling",
          children: "tunneling documentation"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "required-permissions",
        children: "Required permissions"
      }), "\n", _jsx(_components.p, {
        children: "The user credentials must have one of the following IAM roles in the Cloud project you are using. If you don't have any of these roles, contact your Organization Administrator to request access."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "roles/alloydb.admin"
          }), " (the AlloyDB Admin predefined IAM role)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "roles/owner"
          }), " (the Owner basic IAM role)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "roles/editor"
          }), " (the Editor basic IAM role)"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "To create private services access configuration, you also must have these IAM permissions:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "compute.networks.list"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "compute.addresses.create"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "compute.addresses.list"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "servicenetworking.services.addPeering"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["To ensure your credentials are correct, click ", _jsx(_components.strong, {
          children: "Test connection"
        }), ". This confirms\nif Hightouch is able to properly connect to your database by running a simple ", _jsx(_components.code, {
          children: "SELECT"
        }), " query."]
      }), "\n", _jsx(_components.h2, {
        id: "syncing",
        children: "Syncing"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports ", _jsx(_components.strong, {
          children: "Upsert"
        }), " mode, with the option to delete removed rows, and ", _jsx(_components.strong, {
          children: "Insert"
        }), " mode\nusing the ", _jsx(_components.code, {
          children: "COPY FROM"
        }), " statement."]
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch requires a unique identifier in the table you are syncing to in order to add, remove, and update rows.\nThe main identifier column in your sync configuration must match on a ", _jsx(_components.code, {
          children: "UNIQUE"
        }), " or ", _jsx(_components.code, {
          children: "PRIMARY KEY"
        }), " column within your database."]
      }), "\n", _jsx(_components.p, {
        children: "You should see those columns as available options under the records matching section. If there are no fields in the dropdown,\nyou need to add a unique column type to your table."
      }), "\n", _jsx(_components.h3, {
        id: "column-types",
        children: "Column types"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch works out of the box with all standard column types, including:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "BIGINT"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "INT"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "TEXT"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "VARCHAR"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "TIMESTAMPTZ"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "BOOLEAN"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "DECIMAL"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "If you see type errors, it may be because your SQL query is producing the wrong format."
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Hightouch doesn't support arrays of ", _jsx(_components.code, {
            children: "USER-DEFINED"
          }), " types that are JSON\nobjects. We do support arrays of regular JSON objects (JSON or JSONB).\nright now."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "batch-size",
        children: "Batch size"
      }), "\n", _jsx(_components.p, {
        children: "You can tune the number of rows that Hightouch upserts or inserts per query based on your needs and database threshold.\nThe default is 1k rows per batch."
      }), "\n", _jsx(_components.p, {
        children: "If you want to improve the sync's speed performance, you can increase\nthe batch size to a higher number like 10k. However, keep in mind that AlloyDB will fail the entire batch\nof rows if it detects any erroneous row. If you suspect that you will have many bad rows, you should\nnot have a high batch size. Also make sure you account for your database's capacity when increasing the\nbatch size to avoid any locks."
      }), "\n", _jsx(_components.h3, {
        id: "insert-mode",
        children: "Insert mode"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports insert mode through the ", _jsx(_components.code, {
          children: "COPY FROM STDIN"
        }), " AlloyDB statement. This will load the\nqueried rows into your table. However, AlloyDB will throw an error and reject the entire batch if any row\nalready exists or the row contains a primary key or unique value that already exists."]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "https://www.postgresql.org/docs/current/sql-copy.html",
          children: _jsx(_components.code, {
            children: "COPY FROM"
          })
        }), " statement should be faster than the regular statement we use for Upsert mode so we recommend\nselecting Insert mode if you are only inserting rows and want to sync your data faster."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["The ", _jsx(_components.code, {
            children: "COPY FROM STDIN"
          }), " statement should support the same column types as Upsert\nmode, but the array of JSONB type (", _jsx(_components.code, {
            children: "jsonb[]"
          }), ") column. Instead, you will need\nto use a JSONB column type, which supports an array format."]
        })
      })]
    });
  }
}
export default MDXContent;
